import { BaseSuccessResponse, PaginatedData } from "../../response";
import type { HalleDashboardDraftCard } from "./dashboards";
import type { HalleReportDraftCard } from "./scheduled";

export * from "./dashboards";
export * from "./scheduled";

/*
 * Hall-E, the per-company assistant in SageMIN, and its control-panel setup.
 * The contract is claude/design/hall-e/02-architecture.md §2.6 (admin routes
 * under `/api/halle/`, control-panel routes under `/api/halle-platform/`,
 * the WebSocket, and the list of changes made while amphitheater built
 * phase 2). Shapes the contract leaves open follow amphitheater
 * `feature/hall-e` as built (`halle/apis/*`, `halle_platform/apis/cpanel.py`,
 * `halle/consumers.py`).
 *
 * Money rule: an admin response never carries a USD amount or a `*_usd`
 * field. Amounts are NPR decimal strings with two decimals (`"493.36"`),
 * formatted on screen with Nepali digit grouping (`formatNpr`). Only the
 * control panel's internal usage route carries `cost_usd`.
 *
 * Bodies arrive in amphitheater's usual `{data, message, status_code}`
 * envelope, which the services also accept bare.
 */

/** An NPR amount as a decimal string, e.g. `"125000.00"`. Never a float. */
export type HalleNpr = string;

/* ------------------------------------------------------------------ errors */

/**
 * 400: an organization-wide Anthropic key sent without the workspace to use
 * it in (Q32: such a key works only when every request names a workspace).
 * As amphitheater spells it (`halle_platform/workspaces.py`, `6ce1a018`), on
 * both the SageMIN BYOK and the cpanel PAYG key routes. The error copy, the
 * screens, the mocks and the tests read the codes from here.
 */
export const HALLE_KEY_NEEDS_WORKSPACE = "KEY_NEEDS_WORKSPACE";

/**
 * 400: the workspace ID sent with a key is refused: not a workspace ID (the
 * server checks `wrkspc_` + 8 to 64 letters and digits before calling
 * Anthropic), or Anthropic says it is not valid or can't find it for this key.
 */
export const HALLE_WORKSPACE_INVALID = "WORKSPACE_INVALID";

/**
 * The `code` of a refused Hall-E request. Errors have the shape of the
 * existing FEATURE_DISABLED 403: `{code, detail, status_code}`.
 */
export type HalleErrorCode =
  | "FEATURE_DISABLED"
  | "HALLE_PAUSED"
  | "HALLE_KEY_FAILED"
  | "HALLE_NOT_READY"
  | "HALLE_SCOPE"
  /** 409: a turn is running, a concurrency limit was hit, or Anthropic could not be reached to test a key. */
  | "HALLE_BUSY"
  /** 400: a malformed body or parameter; `detail` says what. */
  | "INVALID"
  /** 400: Anthropic refused the company's key on the test call. */
  | "BYOK_INVALID"
  /** 400: Anthropic refused Picovico's key on the test call (control panel). */
  | "KEY_INVALID"
  /** 400: a live note already has that title. */
  | "TITLE_TAKEN"
  /** 400: a common note or a persona version names a company or hall (control panel). */
  | "NAMES_COMPANY"
  /** 403: the support viewer without a live grant of one's own (control panel). */
  | "SUPPORT_ACCESS"
  /** 403: the monthly limit is Picovico's to set (no `admin_can_raise_cap`). */
  | "FORBIDDEN"
  /** 404: not the caller's conversation, or gone. */
  | "NOT_FOUND"
  /** 503: the server cannot store keys (`HALLE_CREDENTIAL_KEYS` unset). */
  | "HALLE_NOT_CONFIGURED"
  /** 400: a connection test before the connection is saved and has a key (control panel). */
  | "NOT_SET_UP"
  /** 400: an organization-wide key without a workspace ID (Q32). */
  | typeof HALLE_KEY_NEEDS_WORKSPACE
  /** 400: a malformed workspace ID, or one Anthropic refused for this key (Q32). */
  | typeof HALLE_WORKSPACE_INVALID;

export interface HalleErrorBody {
  code: HalleErrorCode | string;
  detail?: string;
  status_code?: number;
}

/* ---------------------------------------------------------------- shared */

export type HalleMode = "payg" | "byok";

/** The AD/BS toggle of the admin navbar, sent so answers write dates that way. */
export type HalleCalendar = "AD" | "BS";

/** A write-only secret as the server describes it: never the value itself. */
export interface HalleSecretStatus {
  set: boolean;
  /** `""` (or null) when nothing is saved. */
  last4: string | null;
  verified_at: string | null;
}

/**
 * An Anthropic key as the server describes it: the company's own (BYOK) or
 * Picovico's Pay-as-you-go key. An organization-wide key also names the
 * workspace it runs in (Q32); that ID is not a secret and comes back as it is.
 */
export interface HalleAnthropicKeyStatus extends HalleSecretStatus {
  /** `wrkspc_…` for an organization-wide key; null when there is none. */
  workspace_id: string | null;
}

/**
 * PUT of a key (BYOK, PAYG): tested before it is saved. A key saved without
 * `workspace_id` clears the one saved before.
 */
export interface HalleKeyBody {
  api_key: string;
  /** Only for an organization-wide key: the Anthropic workspace to use it in (`wrkspc_…`). */
  workspace_id?: string;
}

/** The reply to a key PUT. */
export interface HalleKeySaved {
  last4: string;
  workspace_id: string | null;
  verified_at: string | null;
}

/**
 * The reply to the BYOK DELETE: the key fields all empty, plus the mode
 * Hall-E runs on now (review finding 12). "payg" when Pay-as-you-go took
 * over from the removed key; "byok" when Hall-E waits for a new key.
 */
export interface HalleByokRemoved extends HalleKeySaved {
  active_mode: HalleMode | "" | null;
}

export interface HalleHall {
  idx: string;
  name: string;
}

export interface HalleUserRef {
  idx: string;
  name: string;
}

/** A company as a row names it: `{idx, name}`, or just its name (feedback rows). */
export type HalleCompanyRef = string | { idx: string; name: string };

export type HalleExportFormat = "pdf" | "csv";

/* ------------------------------------------------------------- admin: me */

/** `ok`, or why Hall-E can't answer right now. `warn` = past the warn %. */
export type HalleState = "ok" | "warn" | "paused" | "key_failed" | "not_ready";

/** GET `/halle/me/`. */
export interface HalleMe {
  /**
   * The person can use Hall-E here: the flag is on (else the call is a 403
   * FEATURE_DISABLED), they hold `can-use-halle` and have at least one hall.
   */
  enabled: boolean;
  acts: {
    use: boolean;
    settings: boolean;
    knowledge: boolean;
    audit: boolean;
    /**
     * Scheduled reports (06 §6, §13): `can-schedule-halle-reports` (own
     * reports), `can-manage-halle-reports` (everyone's). Absent from servers
     * before them. Reading company data needs no tag of its own (`use`).
     */
    schedule?: boolean;
    manage_reports?: boolean;
    /**
     * Dashboard views (07 §6.3, §12): `can-build-halle-dashboards` (own
     * views) and `can-manage-halle-dashboards` (everyone's, the company
     * default). Absent from servers before them.
     */
    dashboards_build?: boolean;
    dashboards_manage?: boolean;
  };
  /** The asker's scope: the halls Hall-E may read for them. */
  halls: HalleHall[];
  /** Whether Hall-E can answer right now. */
  state: HalleState;
  language_default: string;
  /**
   * Holders of `can-manage-halle-settings` only, and `null` when the company
   * neither runs on Pay-as-you-go nor has its fallback on. Nobody else gets
   * amounts. `pct` is billed paisa × 100 // limit paisa (100 = the limit is
   * reached), `null` while the limit is 0 (none set).
   */
  limit?: { used_npr: HalleNpr; cap_npr: HalleNpr; pct: number | null } | null;
}

/* ---------------------------------------------------- admin: conversations */

/** `"all"` (every hall in scope) or one hall idx. */
export type HalleScope = string;

/**
 * What a chat is about (07 §14.5): from its binding, else its latest draft
 * thread; a chat with neither is a `question`.
 */
export type HalleConversationKind = "dashboard" | "report" | "question";

/** The view or report a chat changes or made (07 §14.6). */
export interface HalleConversationTarget {
  kind: "dashboard" | "report";
  /** The saved view or report; null while it is only a draft. */
  idx: string | null;
  name: string;
  state: "draft" | "saved" | "closed";
  /** The draft's version; null once there is none. */
  version: number | null;
}

/**
 * What the chat has cost, the caller's own (07 §14.6): the sum of its
 * usage events (answers, builder runs, the title) in NPR at each event's
 * stored rate. Everyone sees their own chats' costs.
 */
export interface HalleConversationCost {
  /** NPR decimal string. */
  amount: string;
  answers: number;
  builds: number;
}

/** Conversations are identified by the repo's prefixed `idx` (`hcv_…`, 02 §2.7). Opaque. */
export interface HalleConversation {
  idx: string;
  title: string;
  updated_at: string;
  pinned: boolean;
  /** `active`, or `archived` once deleted. */
  status: string;
  /** The halls it is about: `"all"` or one hall idx. */
  scope: HalleScope;
  /* Round 2 of dashboard views (07 §14.6); absent from an older server. */
  kind?: HalleConversationKind;
  target?: HalleConversationTarget | null;
  /** Plain words: 'Changes your view “Audience engagement” · draft v2'. */
  subtitle?: string;
  cost?: HalleConversationCost;
  /** Who wrote the title: Hall-E after the first answer, the first message, or the person. */
  title_source?: "ai" | "first_message" | "person";
}

/** GET `/halle/conversations/`: a page, filtered by kind and searched (07 §14.6). */
export interface HalleConversationListQuery {
  page?: number;
  kind?: HalleConversationKind;
  /** Searches the title, the subtitle and the target's name. */
  q?: string;
}

/** GET `/halle/conversations/summary/` (`halle-conversations-summary`). */
export interface HalleConversationSummary {
  /** "October 2026". */
  month_label: string;
  /** NPR decimal string: this month's cost of the caller's chats. */
  amount: string;
  chats: number;
}

export interface HalleConversationCreateBody {
  scope?: HalleScope;
  calendar?: HalleCalendar;
  /**
   * One of the caller's conversations: the new one starts from a summary of
   * it, written by its first turn (the "start a new conversation" offer).
   */
  continue_from?: string;
  /** The SageMIN page the person is on (02 §11.5); absent: no page context. */
  page?: HallePageContext;
}

export interface HalleConversationCreated {
  idx: string;
  title: string;
  scope: HalleScope;
}

export interface HalleConversationUpdateBody {
  title?: string;
  pinned?: boolean;
}

/* ---------------------------------------------------------- admin: entries */

export type HalleEntryKind =
  | "user"
  | "assistant"
  | "progress"
  | "note"
  | "notice"
  /** Phase 4 (02 §11.7): a file Hall-E made, and a chart. */
  | "file"
  | "chart"
  /** A proposed scheduled report (06 §5.7): the chat's card. */
  | "report_draft"
  /** A proposed dashboard view (07 §5, §12): the chat's card. */
  | "dashboard_draft";

export type HalleRating = "up" | "down";

export type HalleNoteScope = "company" | "hall";

export type HalleNoteStatus = "proposed" | "approved" | "rejected" | "archived";

/**
 * Codes a `notice` entry carries (and an `error` frame, for the refusals).
 * The entry's `text` is already in the conversation's language.
 */
export type HalleNoticeCode =
  | "STOPPED"
  | "REFUSAL"
  | "TIMEOUT"
  | "BUDGET"
  | "CUT_SHORT"
  | "TOO_LONG"
  /** The conversation is getting long: offer a fresh one with a summary. */
  | "LONG_CONVERSATION"
  | "NO_ACCESS"
  | "ERROR"
  | "HALLE_PAUSED"
  | "HALLE_KEY_FAILED"
  | "HALLE_NOT_READY"
  | "HALLE_BUSY";

interface HalleEntryBase {
  /** Per-conversation order, shared by every kind (Fleet's model). */
  seq: number;
  created_at: string | null;
}

export interface HalleUserEntry extends HalleEntryBase {
  kind: "user";
  text: string;
  /** What the person attached to the question (02 §11.7); absent before phase 4. */
  attachments?: HalleAttachmentRef[];
}

export interface HalleAssistantEntry extends HalleEntryBase {
  kind: "assistant";
  /** Markdown, no raw HTML. */
  text: string;
  feedback: HalleRating | null;
}

/** A plain-words label for what Hall-E is checking. Never a tool or SQL. */
export interface HalleProgressEntry extends HalleEntryBase {
  kind: "progress";
  label: string;
  done: boolean;
}

/** The proposed-note line under an answer. */
export interface HalleNoteEntry extends HalleEntryBase {
  kind: "note";
  scope: HalleNoteScope;
  hall_name?: string | null;
  title: string;
  text: string;
  status: HalleNoteStatus;
}

/** Stopped, paused, key failed, refusal… with the text to show. */
export interface HalleNoticeEntry extends HalleEntryBase {
  kind: "notice";
  code: HalleNoticeCode | string;
  text: string;
}

/* ------------------------------------- admin: files, charts, attachments */

/**
 * The page the person is looking at (02 §11.5), sent with a question and on
 * conversation create. Hall-E re-reads that page's data on the server with
 * the person's own permissions; the browser never sends the data. Over a
 * limit the server ignores the whole thing (no page context).
 */
export interface HallePageContext {
  /** The pathname, ≤ 200 characters. */
  path: string;
  /** The page's search parameters, ≤ 20 keys, each value ≤ 200 characters. */
  query: Record<string, string>;
  /** The route's name from SageMIN's `routes.tsx`, ≤ 120 characters. */
  title: string;
}

export type HalleAttachmentStatus = "ready" | "rejected";

/**
 * 201 of POST `/halle/conversations/<idx>/attachments/` (multipart `file`).
 * The type is sniffed from the bytes on the server. A file it refuses comes
 * back `rejected` with a plain `reject_reason`; only `ready` ones may be sent.
 */
export interface HalleAttachmentUpload {
  idx: string;
  name: string;
  mime: string;
  /** Bytes. */
  size: number;
  /** PDF pages (≤ 50); null for other files. */
  pages: number | null;
  status: HalleAttachmentStatus;
  reject_reason: string | null;
}

/** An attachment as a user entry shows it. */
export interface HalleAttachmentRef {
  idx: string;
  name: string;
  mime: string;
  size: number;
  pages: number | null;
}

export type HalleFileFormat = "pdf" | "csv" | "xlsx";

/** A file Hall-E made: downloaded from GET `/halle/files/<idx>/`. */
export interface HalleFileArtifact {
  idx: string;
  /** Sanitised, with its extension. */
  name: string;
  mime: string;
  /** Bytes. */
  size: number;
  format: HalleFileFormat;
}

export interface HalleFileEntry extends HalleEntryBase {
  kind: "file";
  artifact: HalleFileArtifact;
}

/**
 * Phase 4's types, then the dashboard views' round 2 (07 §14.6): stacked
 * and horizontal bars, and a combo of bars and lines.
 */
export type HalleChartType =
  | "bar"
  | "line"
  | "area"
  | "pie"
  | "donut"
  | "stacked_bar"
  | "horizontal_bar"
  | "combo";

/** How the y values read: rupees, whole numbers, or percent (12.5 = 12.5%). */
export type HalleChartYFormat = "money" | "int" | "percent";

/**
 * One series. In a `combo`, `type` says bars or a line (absent: the first is
 * bars, the rest lines). A line or area chart with the previous period
 * (07 §14.6 `overlay: "previous"`) carries it as extra series marked
 * `previous` ("Tickets (before)"), point for point with `categories`: drawn
 * dashed and muted.
 */
export interface HalleChartSeries {
  name: string;
  data: number[];
  type?: "bar" | "line";
  previous?: boolean;
  /**
   * How this series' values read (amphitheater round 2, after the desk
   * check): money, int or percent. A combo of rupees and tickets has a
   * second y-axis for the series whose format differs from the first.
   * Absent: the chart's `y_format`.
   */
  format?: HalleChartYFormat | string;
}

/**
 * An apexcharts-ready chart (02 §11.7): ≤ 500 points, ≤ 6 series; a pie or
 * donut has one series and ≤ 20 slices (`categories` are its labels).
 */
export interface HalleChartSpec {
  type: HalleChartType;
  title: string;
  categories: string[];
  series: HalleChartSeries[];
  /** The first series' format, and that of any series without its own. */
  y_format: HalleChartYFormat;
}

export interface HalleChartArtifact {
  idx: string;
  title: string;
  spec: HalleChartSpec;
}

export interface HalleChartEntry extends HalleEntryBase {
  kind: "chart";
  artifact: HalleChartArtifact;
}

/** The entries the `artifact` frame carries. */
export type HalleArtifactEntry = HalleFileEntry | HalleChartEntry;

/**
 * A proposed scheduled report (06 §5.7): a snapshot of the draft when Hall-E
 * proposed it, with `draft.status` the thread's status now. Also arrives as
 * an `artifact` frame. Clients before scheduled reports skip it.
 */
export interface HalleReportDraftEntry extends HalleEntryBase {
  kind: "report_draft";
  draft: HalleReportDraftCard;
}

/**
 * A proposed dashboard view (07 §12): a snapshot of the draft version Hall-E
 * built. Each version is its own entry; also arrives as an `artifact` frame.
 * Clients before dashboard views skip it.
 */
export interface HalleDashboardDraftEntry extends HalleEntryBase {
  kind: "dashboard_draft";
  draft: HalleDashboardDraftCard;
}

/** What users see. Tool inputs and outputs are never sent. */
export type HalleEntry =
  | HalleUserEntry
  | HalleAssistantEntry
  | HalleProgressEntry
  | HalleNoteEntry
  | HalleNoticeEntry
  | HalleFileEntry
  | HalleChartEntry
  | HalleReportDraftEntry
  | HalleDashboardDraftEntry;

/** GET `/halle/conversations/<idx>/entries/?before_seq=`. */
export interface HalleEntriesPage {
  entries: HalleEntry[];
  has_more: boolean;
}

export interface HalleMessageBody {
  text: string;
  scope?: HalleScope;
  calendar?: HalleCalendar;
  /** The page the person is on (02 §11.5); left out once they stop sharing it. */
  page?: HallePageContext;
  /** ≤ 5 of the caller's unsent, ready attachments in this conversation (idx). */
  attachments?: string[];
}

/** 202 of POST `…/messages/`: the question's seq. */
export interface HalleMessageAccepted {
  seq: number;
}

/** POST `…/stream-ticket/`: one use, bound to user, device and conversation. */
export interface HalleStreamTicket {
  ticket: string;
  expires_in: number;
  /** `/ws/halle/<idx>/`, appended to the tenant's WebSocket origin. */
  ws_path: string;
}

export interface HalleFeedbackBody {
  /** The conversation idx. */
  conversation: string;
  seq: number;
  rating: HalleRating;
  comment?: string;
}

/** 201 of POST `/halle/feedback/` (an upsert per answer and person). */
export interface HalleFeedbackResult {
  seq: number;
  rating: HalleRating;
}

/* -------------------------------------------------------- admin: WebSocket */

/** A progress step as `partial` carries it (no seq). */
export interface HalleLiveStep {
  label: string;
  done: boolean;
}

/**
 * Server → client frames on `wss://<tenant>/ws/halle/<idx>/?ticket=…[&since=<seq>]`,
 * each a JSON object `{type, …fields}`. A bad Origin, ticket, host or access
 * refuses the handshake (HTTP 403) before any frame. The client sends
 * nothing but `{type: "pong"}`. No `usage` frame exists: amounts stay off
 * the socket.
 */
export type HalleSocketEvent =
  /** First frame. `running_seq`: the question being answered. */
  | { type: "ready"; last_seq: number; running?: boolean; running_seq?: number }
  /**
   * After `ready` when a turn runs: the answer streamed so far (`offset` is
   * always 0, the whole text) under `seq`, and the turn's steps so far.
   * `seq` is null while Hall-E is still looking things up.
   */
  | { type: "partial"; seq: number | null; text: string; offset: number; steps: HalleLiveStep[] }
  /** The visible entries after `since`, or the latest page without it. */
  | { type: "history"; entries: HalleEntry[] }
  | { type: "progress"; seq: number; label: string; done: boolean }
  /**
   * Text appended to the answer under `seq`, never stored. `offset` is where
   * it starts in that answer, in UTF-16 code units (a JS string's length).
   */
  | { type: "delta"; seq: number; offset?: number; text: string }
  /**
   * The live text of `seq` is void: the output check withheld it (the
   * rewrite streams under a later seq), or a retry streams `seq` again from 0.
   */
  | { type: "withdrawn"; seq: number }
  /** The final stored entry. */
  | { type: "message"; entry: HalleEntry }
  | { type: "note"; entry: HalleEntry }
  /**
   * A `file` or `chart` entry Hall-E made (02 §11.7), a `report_draft`
   * (06 §5.7) or a `dashboard_draft` (07 §12).
   */
  | {
      type: "artifact";
      entry: HalleArtifactEntry | HalleReportDraftEntry | HalleDashboardDraftEntry;
    }
  /** A turn has started in this conversation. */
  | { type: "busy" }
  | { type: "done"; seq: number }
  | { type: "error"; code: string; detail?: string }
  /** Sent only to holders of `can-manage-halle-settings`. */
  | { type: "cap_warning"; pct: number }
  | { type: "ping" };

/* ------------------------------------------------ admin: settings and money */

/** GET/PATCH `/halle/settings/` (`can-manage-halle-settings`). */
export interface HalleSettings {
  /** `""` (or null) while no mode is chosen. */
  mode: HalleMode | "" | null;
  allowed_modes: HalleMode[];
  byok: HalleAnthropicKeyStatus & {
    /** Anthropic stopped accepting the saved key. */
    failed: boolean;
  };
  fallback_to_payg: boolean;
  company_instructions: string;
  limit: {
    /** Null when Pay-as-you-go is not allowed. */
    cap_npr: HalleNpr | null;
    warn_pct: number;
    /** Picovico lets the company admin raise the limit (only upwards). */
    can_raise: boolean;
  };
}

export interface HalleSettingsUpdateBody {
  mode?: HalleMode;
  fallback_to_payg?: boolean;
  /** Up to 2,000 characters, no credentials. */
  company_instructions?: string;
  /** Only when `limit.can_raise`, and only upwards. */
  limit?: { cap_npr: HalleNpr };
}

export interface HalleUsageHallRow {
  hall_idx: string;
  name: string;
  questions: number;
  /** Billed Pay-as-you-go rupees, markup included. */
  amount_npr: HalleNpr;
  /** Estimated rupees of the use on the company's own key (not billed); "0.00" when none. */
  byok_estimated_npr: HalleNpr;
}

export interface HalleUsageUserRow {
  user_idx: string;
  name: string;
  questions: number;
  /** Billed Pay-as-you-go rupees, markup included. */
  amount_npr: HalleNpr;
  /** Estimated rupees of the use on the company's own key (not billed); "0.00" when none. */
  byok_estimated_npr: HalleNpr;
}

/**
 * GET `/halle/usage/?month=YYYY-MM`. Billed and estimated rupees are never
 * added together (review finding 6): `total_npr` and each row's
 * `amount_npr` are the billed Pay-as-you-go use (markup included), what the
 * statement shows; `byok_estimated_npr` is the use on the company's own
 * key, at the rate of the day it was used, which Anthropic bills and
 * Picovico does not. A month can hold both (a key test, the fallback, a
 * change of mode).
 */
export interface HalleUsage {
  month: string;
  /** The company's mode today, not the month's. */
  mode: HalleMode | "" | null;
  /** Billed Pay-as-you-go rupees of the month. */
  total_npr: HalleNpr;
  /** Estimated rupees on the company's own key; "0.00" when none. */
  byok_estimated_npr: HalleNpr;
  /** True only while the company runs on its own key (`mode` is byok). */
  estimated: boolean;
  /**
   * Set whenever Pay-as-you-go is allowed and a limit is set, whatever the
   * mode; `pct` is the billed amount against it.
   */
  cap_npr: HalleNpr | null;
  pct: number | null;
  questions: number;
  by_hall: HalleUsageHallRow[];
  by_user: HalleUsageUserRow[];
}

export type HalleStatementStatus = "draft" | "final" | "invoiced" | "paid";

/** GET `/halle/statements/`: finalized ones only, NPR only. */
export interface HalleStatement {
  idx: string;
  number: string | null;
  period: string;
  status: HalleStatementStatus;
  total_npr: HalleNpr;
}

/** GET `/halle/support-access/`: every time Picovico support opened a chat. */
export interface HalleSupportAccessRow {
  at: string;
  staff_name: string;
  /** `""` when support opened the list rather than one conversation. */
  conversation_title: string | null;
  reason: string;
}

/* -------------------------------------------------------- admin: knowledge */

/** A company note names one of the company's halls (each hall named, once). */
export interface HalleKnowledgeWarning {
  code: "names_hall";
  hall_idx: string;
  hall_name: string;
}

export interface HalleKnowledgeSource {
  kind: "halle" | "person";
  by_name: string;
  /** The conversation Hall-E suggested it in. */
  conversation_idx?: string | null;
  conversation_title?: string | null;
  at: string | null;
}

/** GET `/halle/knowledge/?status=` (`can-review-halle-knowledge`). */
export interface HalleKnowledgeNote {
  idx: string;
  scope: HalleNoteScope;
  hall_idx?: string | null;
  hall_name?: string | null;
  title: string;
  text: string;
  status: HalleNoteStatus;
  pinned: boolean;
  source: HalleKnowledgeSource;
  /** One short line on why Hall-E suggested it. */
  evidence?: string | null;
  warns: HalleKnowledgeWarning[];
}

/** POST `/halle/knowledge/`: a note written by a person, approved at once. */
export interface HalleKnowledgeCreateBody {
  scope: HalleNoteScope;
  hall_idx?: string | null;
  title: string;
  text: string;
}

export interface HalleKnowledgeUpdateBody {
  title?: string;
  text?: string;
  scope?: HalleNoteScope;
  hall_idx?: string | null;
  pinned?: boolean;
}

/* ------------------------------------------------------------ admin: audit */

export type HalleAuditFlag = "out_of_scope_ask" | "answer_rewritten" | "thumbs_down";

/** GET `/halle/audit/conversations/` (`can-audit-halle-chats`). */
export interface HalleAuditConversation {
  idx: string;
  user: HalleUserRef;
  scope_label: string;
  title: string;
  updated_at: string | null;
  flags: HalleAuditFlag[];
}

export interface HalleAuditQuery {
  user?: string;
  hall?: string;
  from?: string;
  to?: string;
}

/* ---------------------------------------------------------------- cpanel */

/** GET `/halle-platform/companies/`: the company picker. */
export interface HalleCompanyRow {
  idx: string;
  name: string;
  feature_enabled: boolean;
  allowed_modes: HalleMode[];
  active_mode: HalleMode | "";
}

/** GET/PUT `/halle-platform/companies/<idx>/config/` (`can-manage-halle-connections`). */
export interface HalleCompanyConfig {
  allowed_modes: HalleMode[];
  markup_pct: string;
  monthly_cap_npr: HalleNpr;
  warn_pct: number;
  retention_days: number;
  /** `""` (or null) for the platform default. */
  model_override: string | null;
  effort_override: string | null;
  /**
   * The report builder's model for this company (06 §23): `""` (or null)
   * for the platform's `builder_model`, else a model with a price row in
   * effect (amphitheater refuses any other with 400 `INVALID`). Absent from
   * an older server.
   */
  builder_model_override?: string | null;
  /** The company admin may raise the monthly limit in SageMIN. */
  admin_can_raise_cap: boolean;
  /** Read-only. */
  active_mode: HalleMode | "" | null;
  /** Read-only. */
  byok_set: boolean;
  /** Read-only: the company flag `ai.halle`. */
  feature_enabled: boolean;
}

export type HalleCompanyConfigBody = Pick<
  HalleCompanyConfig,
  | "allowed_modes"
  | "markup_pct"
  | "monthly_cap_npr"
  | "warn_pct"
  | "retention_days"
  | "model_override"
  | "effort_override"
  | "builder_model_override"
  | "admin_can_raise_cap"
>;

/** GET `/halle-platform/companies/<idx>/usage/?month=`: internal, staff only. */
export interface HalleCompanyUsage {
  month?: string;
  billed_npr: HalleNpr;
  questions: number;
  cost_usd: string;
  internal_cost_usd: string;
}

export type HalleHallConnectionStatus = "none" | "key" | "connected" | "failing";

/** GET `/halle-platform/companies/<idx>/halls/`. */
export interface HalleHallRow {
  hall_idx: string;
  name: string;
  status: HalleHallConnectionStatus;
  last_test_at: string | null;
  last_test_ok: boolean | null;
  /** `""` (or null) before a connection exists. */
  signoz_service_name: string | null;
}

/** GET `/halle-platform/halls/<idx>/`. */
export interface HalleHallDetail {
  hall_idx: string;
  name: string;
  company_idx: string;
  status: HalleHallConnectionStatus;
}

/**
 * PUT `/halle-platform/halls/<idx>/connection/`. `db_password` is write-only;
 * the client leaves it out to keep the saved one.
 */
export interface HalleHallConnectionBody {
  ssh_host: string;
  ssh_port: number;
  ssh_user: string;
  db_port: number;
  db_name: string;
  db_user: string;
  db_password?: string;
  signoz_connection: string | null;
  signoz_service_name: string;
  /**
   * Forget the pinned host key (the hall server was reinstalled). A new SSH
   * address forgets it anyway; any change needs a new connection test.
   */
  reset_host_key?: boolean;
}

/**
 * GET `/halle-platform/halls/<idx>/connection/`: the saved settings without
 * any secret (defaults before one exists). The PUT answers the same.
 */
export interface HalleHallConnection
  extends Omit<HalleHallConnectionBody, "db_password" | "reset_host_key"> {
  /** Where the tunnel ends on the hall (read-only, the loopback). */
  db_host?: string;
  db_password_set: boolean;
  public_key: string;
  host_key_fingerprint: string;
  host_key_pinned_at?: string | null;
  /** When the read-only login last worked in a connection test. */
  db_verified_at?: string | null;
  status: HalleHallConnectionStatus;
}

/** POST `…/connection/generate-key/`. The private key never leaves the server. */
export interface HalleGeneratedKey {
  public_key: string;
  authorized_keys_line: string;
  role_sql: string;
}

export interface HalleConnectionCheck {
  key: string;
  label: string;
  ok: boolean;
  detail?: string | null;
}

/** POST `…/connection/test/`. A failed `admin_login_refused` blocks the hall. */
export interface HalleConnectionTest {
  ok: boolean;
  host_key_fingerprint: string | null;
  checks: HalleConnectionCheck[];
}

/**
 * The report builder's `output_config.effort` (06 §23.2): all five that
 * Opus 5.5 takes. The chat's `effort` keeps low, medium and high.
 */
export type HalleBuilderEffort = "low" | "medium" | "high" | "xhigh" | "max";

/**
 * What a Hall-E usage event was for (amphitheater `UsagePurpose`). No
 * response lists usage by purpose yet; `builder` ("Report builder", 06 §23)
 * is billed like an answer.
 */
export type HalleUsagePurpose =
  | "turn"
  | "codebase"
  | "summary"
  | "key_test"
  | "builder";

/** GET/PATCH `/halle-platform/settings/`. */
export interface HallePlatformSettings {
  model: string;
  effort: string;
  /**
   * The report builder (06 §23): the larger model that writes and changes
   * scheduled reports, once per draft, never per run. Default
   * `claude-opus-5-5`; it must have a price row in effect (else 400
   * `INVALID`). Absent from an older server.
   */
  builder_model?: string;
  /** Default `high`. */
  builder_effort?: HalleBuilderEffort;
  budgets: Record<string, number>;
  /** Read-only. */
  payg_key: HalleAnthropicKeyStatus;
  /** Read-only: the rate in use, null before one is set. */
  exchange_rate: { npr_per_usd: string; effective_from: string } | null;
  /** Read-only: false while the server cannot store keys. */
  server_ready?: boolean;
}

export type HallePlatformSettingsBody = Partial<
  Pick<
    HallePlatformSettings,
    "model" | "effort" | "builder_model" | "builder_effort" | "budgets"
  >
>;

/** GET/POST `/halle-platform/exchange-rates/`, newest first. The first is in use. */
export interface HalleExchangeRate {
  idx?: string;
  npr_per_usd: string;
  effective_from?: string;
  set_by?: string;
  note?: string;
}

/** A `HalleModelPrice` row: Anthropic's USD per million tokens (internal). */
export interface HalleModelPrice {
  idx?: string;
  model: string;
  input_per_mtok: string;
  output_per_mtok: string;
  cache_write_5m_per_mtok: string;
  cache_write_1h_per_mtok: string;
  cache_read_per_mtok: string;
  /** Above this many prompt tokens every rate is the long-context one (all five or none). */
  long_context_threshold_tokens: number | null;
  long_context_input_per_mtok: string | null;
  long_context_output_per_mtok: string | null;
  long_context_cache_write_5m_per_mtok: string | null;
  long_context_cache_write_1h_per_mtok: string | null;
  long_context_cache_read_per_mtok: string | null;
  effective_from: string;
  note?: string;
}

/** `is_current` marks the version in use (02 §2.7). */
export interface HallePromptVersion {
  idx: string;
  version: number;
  is_current: boolean;
  notes?: string;
  created_at: string | null;
  persona_md?: string;
  guardrails_md?: string;
  glossary_md?: string;
}

export interface HallePromptVersionBody {
  persona_md: string;
  guardrails_md: string;
  glossary_md: string;
  notes?: string;
}

/** GET `/halle-platform/prompt-versions/<idx>/diff/`: a unified diff against the current version. */
export interface HallePromptDiff {
  diff: string;
  version: number;
  /** The current version, null when none is current. */
  against: number | null;
}

/** `/halle-platform/common-knowledge/`: the admin note's fields plus its source. */
export interface HalleCommonNote {
  idx: string;
  title: string;
  text: string;
  status: HalleNoteStatus;
  source?: { kind: "halle" | "person"; by_name: string; at: string | null };
  source_company: HalleCompanyRef | null;
  evidence?: string | null;
  auto_rejected_reason: string | null;
  review_note?: string;
}

export interface HalleCommonNoteBody {
  title: string;
  text: string;
}

/** `/halle-platform/signoz-connections/`. The key is write-only. */
export interface HalleSignozConnection {
  idx: string;
  name: string;
  base_url: string;
  api_key: { set: boolean; last4: string | null };
  company?: { idx: string; name: string } | null;
  halls_count: number;
  last_test_at?: string | null;
  last_test_ok?: boolean | null;
}

export interface HalleSignozConnectionBody {
  name: string;
  base_url: string;
  api_key?: string;
}

export interface HalleSignozTest {
  ok: boolean;
  detail?: string | null;
}

/** GET `/halle-platform/statements/?period=`: NPR only, no rate anywhere. */
export interface HallePlatformStatement {
  idx: string;
  company: HalleCompanyRef;
  period: string;
  status: HalleStatementStatus;
  number: string | null;
  /** Usage records summed. */
  event_count?: number;
  /** The exact sum to the paisa, before rounding to whole rupees. */
  subtotal_npr?: HalleNpr;
  rounding_npr?: HalleNpr;
  total_npr: HalleNpr;
}

/** POST `/halle-platform/support/access/`: writes `HalleSupportAccess`. */
export interface HalleSupportAccessBody {
  company_idx: string;
  /** At least 10 characters. */
  reason: string;
}

export interface HalleSupportAccessGrant {
  access_id: string;
  expires_at: string;
}

export interface HalleSupportConversation {
  idx: string;
  title: string;
  user: HalleUserRef;
  updated_at: string | null;
  flags?: HalleAuditFlag[];
  flagged?: boolean;
  /** The person deleted it (still kept until the retention purge). */
  deleted_by_user?: boolean;
}

export type HalleSupportEntryKind =
  | HalleEntryKind
  | "tool_call"
  | "tool_result"
  | "system_note"
  | "hidden_draft"
  | "continuation";

/** One answer's rating as the support viewer shows it. */
export interface HalleSupportFeedback {
  rating: HalleRating;
  comment?: string | null;
  by?: string;
}

/**
 * The technical view (support viewer only): the call's input on
 * `tool_call`, the output, the verdict and the timing on `tool_result`,
 * withdrawn drafts and the ratings.
 */
export interface HalleSupportEntry {
  seq: number;
  kind: HalleSupportEntryKind | string;
  created_at: string | null;
  text?: string | null;
  label?: string | null;
  tool?: string | null;
  input?: unknown;
  output?: unknown;
  ok?: boolean | null;
  allowed?: boolean | null;
  deny_reason?: string | null;
  duration_ms?: number | null;
  /** `hidden_draft`: why the output check withheld it. */
  withheld_because?: string | null;
  /** `notice`. */
  code?: string | null;
  feedback?: HalleSupportFeedback[] | HalleSupportFeedback | null;
  /**
   * `dashboard_draft` (07 §12): the card as the chat showed it, passed
   * through by amphitheater's `technical_entry`. Entries stored before that
   * carry none; the viewer shows their text.
   */
  draft?: HalleDashboardDraftCard | null;
}

/** GET `/halle-platform/support/conversations/<idx>/entries/?access=`. */
export interface HalleSupportTranscript {
  idx?: string;
  title?: string;
  entries: HalleSupportEntry[];
}

/** GET `/halle-platform/feedback/?rating=down`. */
export interface HalleFeedbackRow {
  company: HalleCompanyRef;
  company_idx?: string;
  user_name: string;
  conversation_title: string;
  conversation_idx?: string;
  seq?: number;
  comment: string | null;
  at: string | null;
}

/* ------------------------------------------------------------- envelopes */

export type HalleMeSuccessResponse = BaseSuccessResponse<HalleMe>;
export type HalleConversationListSuccessResponse = BaseSuccessResponse<
  PaginatedData<HalleConversation>
>;
export type HalleConversationSuccessResponse =
  BaseSuccessResponse<HalleConversation>;
export type HalleConversationSummarySuccessResponse =
  BaseSuccessResponse<HalleConversationSummary>;
export type HalleConversationCreatedSuccessResponse =
  BaseSuccessResponse<HalleConversationCreated>;
export type HalleEntriesSuccessResponse = BaseSuccessResponse<HalleEntriesPage>;
export type HalleMessageAcceptedSuccessResponse =
  BaseSuccessResponse<HalleMessageAccepted>;
export type HalleAttachmentUploadSuccessResponse =
  BaseSuccessResponse<HalleAttachmentUpload>;
export type HalleStreamTicketSuccessResponse =
  BaseSuccessResponse<HalleStreamTicket>;
export type HalleFeedbackSuccessResponse =
  BaseSuccessResponse<HalleFeedbackResult>;
export type HalleSettingsSuccessResponse = BaseSuccessResponse<HalleSettings>;
export type HalleKeySavedSuccessResponse = BaseSuccessResponse<HalleKeySaved>;
export type HalleByokRemovedSuccessResponse =
  BaseSuccessResponse<HalleByokRemoved>;
export type HalleUsageSuccessResponse = BaseSuccessResponse<HalleUsage>;
export type HalleStatementListSuccessResponse = BaseSuccessResponse<
  HalleStatement[]
>;
export type HalleSupportAccessListSuccessResponse = BaseSuccessResponse<
  HalleSupportAccessRow[]
>;
export type HalleKnowledgeListSuccessResponse = BaseSuccessResponse<
  HalleKnowledgeNote[]
>;
export type HalleKnowledgeSuccessResponse =
  BaseSuccessResponse<HalleKnowledgeNote>;
export type HalleAuditListSuccessResponse = BaseSuccessResponse<
  HalleAuditConversation[]
>;
export type HalleCompanyListSuccessResponse = BaseSuccessResponse<
  HalleCompanyRow[]
>;
export type HalleCompanyConfigSuccessResponse =
  BaseSuccessResponse<HalleCompanyConfig>;
export type HalleCompanyUsageSuccessResponse =
  BaseSuccessResponse<HalleCompanyUsage>;
export type HalleHallListSuccessResponse = BaseSuccessResponse<HalleHallRow[]>;
export type HalleHallDetailSuccessResponse =
  BaseSuccessResponse<HalleHallDetail>;
export type HalleHallConnectionSuccessResponse =
  BaseSuccessResponse<HalleHallConnection>;
export type HalleGeneratedKeySuccessResponse =
  BaseSuccessResponse<HalleGeneratedKey>;
export type HalleConnectionTestSuccessResponse =
  BaseSuccessResponse<HalleConnectionTest>;
export type HallePlatformSettingsSuccessResponse =
  BaseSuccessResponse<HallePlatformSettings>;
export type HalleExchangeRateListSuccessResponse = BaseSuccessResponse<
  HalleExchangeRate[]
>;
export type HalleExchangeRateSuccessResponse =
  BaseSuccessResponse<HalleExchangeRate>;
export type HalleModelPriceListSuccessResponse = BaseSuccessResponse<
  HalleModelPrice[]
>;
export type HalleModelPriceSuccessResponse =
  BaseSuccessResponse<HalleModelPrice>;
export type HallePromptVersionListSuccessResponse = BaseSuccessResponse<
  HallePromptVersion[]
>;
export type HallePromptVersionSuccessResponse =
  BaseSuccessResponse<HallePromptVersion>;
export type HallePromptDiffSuccessResponse =
  BaseSuccessResponse<HallePromptDiff>;
export type HalleCommonNoteListSuccessResponse = BaseSuccessResponse<
  HalleCommonNote[]
>;
export type HalleCommonNoteSuccessResponse =
  BaseSuccessResponse<HalleCommonNote>;
export type HalleSignozConnectionListSuccessResponse = BaseSuccessResponse<
  HalleSignozConnection[]
>;
export type HalleSignozConnectionSuccessResponse =
  BaseSuccessResponse<HalleSignozConnection>;
export type HalleSignozTestSuccessResponse =
  BaseSuccessResponse<HalleSignozTest>;
export type HallePlatformStatementListSuccessResponse = BaseSuccessResponse<
  HallePlatformStatement[]
>;
export type HallePlatformStatementSuccessResponse =
  BaseSuccessResponse<HallePlatformStatement>;
export type HalleSupportAccessGrantSuccessResponse =
  BaseSuccessResponse<HalleSupportAccessGrant>;
export type HalleSupportConversationListSuccessResponse = BaseSuccessResponse<
  HalleSupportConversation[]
>;
export type HalleSupportTranscriptSuccessResponse =
  BaseSuccessResponse<HalleSupportTranscript>;
export type HalleFeedbackListSuccessResponse = BaseSuccessResponse<
  HalleFeedbackRow[]
>;
